import moment from "moment";
import {
  STATION_TIME_ZONE,
  stationDay,
  stationDayStart,
  stationSlotStart,
} from "../../util/stationTime";
import { setTimeOnDate } from "../../util";

/*
 * Every window below is built on the clock of the location being booked
 * (#3219): `timeZone` is that location's zone — `useSelectedLocationTimeZone` /
 * `selectSelectedLocationTimeZone` — and defaults to `STATION_TIME_ZONE`. The
 * server reads the same window in the same zone.
 */

/**
 * The stay window a room-family request is priced and booked for — ONE
 * definition, used by booking creation (`createVenueReservationFlow`) and by
 * the summary's price quote (`BookingSummaryShared`).
 *
 * They used to disagree: creation stamped the venue's start/end time onto the
 * dates, while the summary quoted the bare dates (local midnight to local
 * midnight). For a same-day venue booking that window is empty, so the server
 * answered 400 and the summary said the price could not be calculated — on
 * every same-day venue booking, while the booking itself stored the right
 * price (#2980 D1). A quote that is not computed for the window being booked
 * is a quote for something else.
 */

/**
 * Venue books hourly and stamps the picked start/end times onto the dates;
 * the apartment request keeps its check-in/check-out dates as they are
 * (`setTimeOnDate` would throw on the never-set venue times).
 */
export const requestWindow = (
  isVenue: boolean,
  fromDate: string,
  toDate: string,
  startTime?: string,
  endTime?: string,
  timeZone: string = STATION_TIME_ZONE
): { fromDate: string; toDate: string } =>
  isVenue
    ? {
        fromDate: setTimeOnDate(fromDate, startTime!, timeZone),
        toDate: setTimeOnDate(toDate, endTime!, timeZone),
      }
    : { fromDate, toDate };

export { STATION_TIME_ZONE, stationDayStart } from "../../util/stationTime";

/**
 * The window the venue SEARCH asks for — the same instants creation and the
 * quote send (`resolveRequestDates` → `requestWindow`): the picked day's
 * station midnight with the picked time stamped on in station time. The search
 * used to put the times on the PHONE's day, so a UTC phone picking 10:00–14:00
 * searched 12:00–16:00 Berlin and then booked 10:00–14:00 Berlin. A side
 * without a picked day or time ("0" is the unset sentinel) is sent empty, as
 * before. Pinned equal to the create window by `venueSearchWindow.test.ts`.
 */
export const venueSearchWindow = (orderData: {
  checkInDate?: moment.Moment | null;
  checkOutDate?: moment.Moment | null;
  startTime?: string | null;
  endTime?: string | null;
}, timeZone: string = STATION_TIME_ZONE): { fromDate: string; toDate: string } => {
  const at = (day?: moment.Moment | null, time?: string | null): string =>
    day && time && time !== "0"
      ? setTimeOnDate(stationDayStart(day, timeZone), time, timeZone)
      : "";
  return {
    fromDate: at(orderData.checkInDate, orderData.startTime),
    toDate: at(orderData.checkOutDate, orderData.endTime),
  };
};

/**
 * The single-day window a station booking (SUP, rental, storage, p2p, parcel)
 * asks for: the picked day's LABEL at the slot's station time — slots are
 * station wall clock, never the phone's (`stationSlotStart`) — or the picked
 * day's station midnight when no time was chosen, plus the duration.
 *
 * The `time === "00:00"` branch (end pulled back one day) is carried over
 * unchanged: it dates from the app's initial import (9b8926070) with no
 * comment, test or caller that states its intent, so it is kept as is.
 */
export const stationSlotWindow = (
  date: moment.Moment,
  time: string | undefined,
  duration: number | undefined,
  timeZone: string = STATION_TIME_ZONE
): { fromDate: string; toDate: string } => {
  const start = time
    ? stationSlotStart(date, time, timeZone)
    : stationDay(date, timeZone);
  const end = start.clone().add(duration, "minutes");
  return {
    fromDate: start.toISOString(),
    toDate:
      time !== "00:00"
        ? end.toISOString()
        : end.subtract(1, "days").toISOString(),
  };
};

/**
 * The requested stay window from the order data: check-in/check-out dates
 * when both are picked (the room-family case, sent as station midnight —
 * see `stationDayStart`), otherwise date + optional time + duration (the
 * single-day shape other flows fill — `stationSlotWindow`).
 */
export const resolveRequestDates = (orderData: {
  checkInDate?: moment.Moment | null;
  checkOutDate?: moment.Moment | null;
  date?: moment.Moment | null;
  time?: string;
  duration?: number;
}, timeZone: string = STATION_TIME_ZONE): { fromDate: string; toDate: string } => {
  if (orderData.checkInDate && orderData.checkOutDate) {
    return {
      fromDate: stationDayStart(orderData.checkInDate, timeZone),
      toDate: stationDayStart(orderData.checkOutDate, timeZone),
    };
  }

  return stationSlotWindow(orderData.date!, orderData.time, orderData.duration, timeZone);
};
